import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAppSelector } from '../store/hooks';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.user);
  const isBlogActive = location.pathname.startsWith('/blog');

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>About Focuzon - Best Focus App for Deep Work | Our Story</title>
        <meta name="description" content="Learn about Focuzon, the best focus app for deep work and concentration. Discover our mission to create a quiet workspace designed around how focus actually works." />
        <meta name="keywords" content="about focuzon, focus app about, deep work app, productivity app story, focus tool mission, meditation workspace" />
        <link rel="canonical" href="https://www.focuzon.com/about" />
        <meta property="og:title" content="About Focuzon - Best Focus App for Deep Work" />
        <meta property="og:description" content="Learn about Focuzon, the best focus app for deep work and concentration. Discover our mission to create a quiet workspace." />
        <meta property="og:url" content="https://www.focuzon.com/about" />
        <meta name="twitter:title" content="About Focuzon - Best Focus App for Deep Work" />
        <meta name="twitter:description" content="Learn about Focuzon, the best focus app for deep work and concentration." />
      </Helmet>
      <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
        {/* Background gradient shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        </div>

        {/* Navigation - Fixed Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 border-b border-orange-100 bg-[#FFF8F0]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo - Left Aligned */}
              <div className="flex items-center justify-center flex-shrink-0 h-full">
                <img 
                  src="/focuzon.png" 
                  alt="Focuzon Logo" 
                  className="h-8 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ maxWidth: '140px', minWidth: '100px' }}
                  onClick={() => navigate('/')}
                />
              </div>

              {/* Navigation Menu - Centered */}
              <div className="hidden lg:flex items-center justify-center flex-1">
                <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-full px-2 py-1.5 border border-orange-100/50 shadow-sm">
                  <button
                    onClick={() => navigate('/')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/'
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    Home
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/blog')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      isBlogActive
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    Blog
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/about')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/about'
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    About Us
                  </button>
                </div>
              </div>

              {/* Auth Buttons - Right Aligned */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                {!isAuthenticated ? (
                  <>
                    <button 
                      onClick={() => navigate('/login')}
                      className="hidden sm:block text-gray-700 hover:text-orange-600 font-medium transition-colors text-sm px-3 py-1.5 rounded-lg hover:bg-orange-50/50"
                    >
                      Log in
                    </button>
                    <button 
                      onClick={() => navigate('/signup')}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={goToDashboard}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                  >
                    Go to Dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Content - Add padding-top for fixed header */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Focuzon
            </h1>
            
            <div className="space-y-8 text-gray-700 leading-relaxed">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-800 font-semibold">
                  Focuzon helps you get into flow. It's that simple.
                </p>
                <p>
                  We created Focuzon to solve a real problem: productivity apps that promise results but deliver noise. While others rely on endless notifications, gamified streaks, and guilt-inducing metrics, we built something different: a tool that actually helps you focus.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">What We're About</h2>
                <p>
                  Focuzon gives you a quiet space to work. Set a timer, pick your focus sounds (or silence), and get things done. We offer simple tools for mental reset—breathing exercises, gentle puzzles, and calming interactions—but these aren't games to win or achievements to unlock. They're quiet tools for when you need a break from focused work. No pressure. Just you and your work.
                </p>
                <p className="mt-4">
                  Work solo or sync up with your team for shared focus sessions. Take notes as ideas come up. See your patterns over time. Everything you need is there when you need it, invisible when you don't.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">A ZFLOC Product</h2>
                <div className="bg-orange-50/50 rounded-xl p-6 border border-orange-200/50">
                  <p className="mb-3">
                    Focuzon is a product of <a href="https://www.zfloc.com/" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 font-medium underline">ZFLOC Technologies</a>, a company that builds exceptional web and mobile applications. Our team brings years of expertise in creating tools that people actually want to use.
                  </p>
                  <a 
                    href="https://www.zfloc.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium transition-colors text-sm"
                  >
                    Learn more about ZFLOC
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Philosophy on Focus</h2>
                <p>
                  Most productivity apps assume you need more willpower. We know you need fewer distractions and better tools. That's why Focuzon stays out of your way: no pop-ups, no "motivational" messages, just a clean workspace when you need it.
                </p>
                <p className="mt-4">
                  Focus isn't about working longer hours. It's about finding those stretches where you're fully present and doing your best work. Focuzon creates those conditions seamlessly, without getting in the way.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Get in Touch</h2>
                <p>
                  Have feedback, questions, or ideas? We'd love to hear from you. Our team reads every message and responds personally.
                </p>
                <div className="bg-orange-50/50 rounded-xl p-6 border border-orange-200/50 mt-4">
                  <a 
                    href="mailto:support@focuzon.com" 
                    className="text-orange-600 hover:text-orange-700 font-medium text-lg transition-colors"
                  >
                    support@focuzon.com
                  </a>
                  <p className="text-sm text-gray-600 mt-2">
                    Every message is read by our team. No bots, no templates. Just genuine, thoughtful responses.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AboutPage;
